import React from 'react'
import styled from 'styled-components'
import { Text } from '@lmc/ui-kit/src/general'
import { useTranslation } from 'react-i18next'
import { NAMESPACE } from 'locale'
import { FormCard } from '@lmc/ui-kit'
import dayjs from 'dayjs'

type AdministrationDetailContentProps = {
  record: any
  medicationName: string
}

export const AdministrationDetailContent: React.FC<
  AdministrationDetailContentProps
> = ({ record }) => {
  const { t } = useTranslation(NAMESPACE.EMAR_V2)

  const isAdministered = record?.isAdministered !== false
  const quantity = record?.quantity || record?.amount1 || 0
  const unit = record?.unit || record?.stockQuantityType || t('TABLETS')
  const outcome = record?.outcome || ''
  const reason = record?.reason || ''
  const description = record?.description || ''
  const scheduledFor = record?.scheduledFor
  const scheduledForDate = scheduledFor
    ? dayjs(scheduledFor).format('DD-MM-YYYY HH:mm')
    : null
  const isPRN = record?.isPRN || false
  const signedBy = record?.signedBy || record?.loggedBy
  const signedByName = signedBy
    ? `${signedBy.name?.first || ''} ${signedBy.name?.last || ''}`.trim()
    : ''
  const signedAt = record?.signedAt || record?.timeLogged
  const signedAtFormatted = signedAt
    ? dayjs(signedAt).format('DD-MM-YYYY HH:mm')
    : ''
  const stockUpdated = record?.stockUpdated
  const stockQuantityRemoved = record?.stockQuantityRemoved || 0
  const stockQuantityLeft = record?.stockQuantityLeft || record?.amount2 || 0
  const expectedStock = record?.expectedStock || record?.amount2 || 0
  const stockUnit = record?.stockUnit || unit

  const descriptionText = isAdministered
    ? `${quantity} ${unit} ${t('ADMINISTERED')}`
    : `Medication was not taken because ${outcome || reason}${
        description ? `, ${description}` : ''
      }`

  const stockUpdatedText =
    stockUpdated && stockQuantityRemoved > 0
      ? `${stockQuantityRemoved} ${stockUnit} ${t('REMOVED_FROM_STOCK')}, ${stockQuantityLeft} ${stockUnit} ${t('LEFT')}`
      : t('NOT_APPLICABLE')

  return (
    <FormCard level={2}>
      <InfoRow>
        <FullWidthSection>
          <StyledLabel level={2}>{t('DESCRIPTION')}</StyledLabel>
          <Text>{descriptionText}</Text>
        </FullWidthSection>
      </InfoRow>
      <InfoRow>
        <InfoSection>
          <StyledLabel level={2}>{t('SCHEDULED_FOR')}</StyledLabel>
          <Text weight="bold">
            {scheduledForDate
              ? `${scheduledForDate}${isPRN ? ` / ${t('PRN')}` : ''}`
              : isPRN
                ? t('PRN')
                : '-'}
          </Text>
        </InfoSection>
        <InfoSection>
          <StyledLabel level={2}>{t('OUTCOME')}</StyledLabel>
          <Text weight="bold">{outcome || '-'}</Text>
        </InfoSection>
        <InfoSection>
          <StyledLabel level={2}>{t('EXPECTED_STOCK')}</StyledLabel>
          <Text weight="bold">
            {expectedStock !== undefined
              ? `${expectedStock} ${stockUnit}`
              : '-'}
          </Text>
        </InfoSection>
      </InfoRow>
      <InfoRow>
        <FullWidthSection>
          <StyledLabel level={2}>{t('STOCK_UPDATED')}</StyledLabel>
          <Text weight="bold">{stockUpdatedText}</Text>
        </FullWidthSection>
      </InfoRow>
    </FormCard>
  )
}

const InfoRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 24px;
`

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  width: 185px;
`

const FullWidthSection = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const StyledLabel = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color5};
`
